/**
 * Timeline da aba Atividade: junta o mural do projeto, os comentários das tarefas vinculadas e as
 * mudanças de status numa linha só, do mais recente pro mais antigo.
 *
 * Agregação no client, como o resto do módulo — são três consultas pequenas e escopadas ao
 * projeto, e uma view no banco seria mais uma coisa pra manter em sincronia.
 */

const STATUS_ROTULO: Record<string, string> = {
  novo: 'Novo',
  em_analise: 'Em análise',
  em_desenvolvimento: 'Em desenvolvimento',
  em_teste: 'Em teste',
  concluido: 'Concluído',
  rejeitado: 'Rejeitado',
};

export interface ComentarioProjetoBruto {
  id: number;
  user_id: string | null;
  conteudo: string;
  created_at: string;
  anexos_url?: string[] | null;
}

export interface ComentarioTarefaBruto {
  id: number;
  tarefa_id: number | string;
  user_id: string | null;
  conteudo: string;
  created_at: string;
  anexos_url?: string[] | null;
}

export interface EventoStatusBruto {
  id: number;
  tarefa_id: number | string;
  user_id: string | null;
  from_status: string | null;
  to_status: string;
  created_at: string;
}

export type OrigemAtividade =
  | { tipo: 'projeto' }
  | { tipo: 'tarefa'; tarefaId: string; titulo: string };

export interface ItemAtividade {
  /** Prefixado pela fonte: id de comentário e de evento colidem entre tabelas. */
  id: string;
  tipo: 'comentario' | 'status';
  createdAt: string;
  userId: string | null;
  conteudo: string;
  anexos: string[];
  origem: OrigemAtividade;
}

interface Entradas {
  comentariosProjeto: ComentarioProjetoBruto[];
  comentariosTarefas: ComentarioTarefaBruto[];
  eventosStatus: EventoStatusBruto[];
  /** id da tarefa (string) -> título, para etiquetar a origem. */
  titulosDeTarefa: Map<string, string>;
}

const rotulo = (status: string | null): string =>
  status ? STATUS_ROTULO[status] || status : '—';

export const montarTimeline = ({
  comentariosProjeto,
  comentariosTarefas,
  eventosStatus,
  titulosDeTarefa,
}: Entradas): ItemAtividade[] => {
  const origemTarefa = (id: number | string): OrigemAtividade => ({
    tipo: 'tarefa',
    tarefaId: String(id),
    titulo: titulosDeTarefa.get(String(id)) || 'Tarefa removida',
  });

  const itens: ItemAtividade[] = [
    ...comentariosProjeto.map(c => ({
      id: `cp-${c.id}`,
      tipo: 'comentario' as const,
      createdAt: c.created_at,
      userId: c.user_id,
      conteudo: c.conteudo,
      anexos: c.anexos_url || [],
      origem: { tipo: 'projeto' as const },
    })),
    ...comentariosTarefas.map(c => ({
      id: `ct-${c.id}`,
      tipo: 'comentario' as const,
      createdAt: c.created_at,
      userId: c.user_id,
      conteudo: c.conteudo,
      anexos: c.anexos_url || [],
      origem: origemTarefa(c.tarefa_id),
    })),
    ...eventosStatus.map(e => ({
      id: `ev-${e.id}`,
      tipo: 'status' as const,
      createdAt: e.created_at,
      userId: e.user_id,
      conteudo: `Mudou o status de "${rotulo(e.from_status)}" para "${rotulo(e.to_status)}"`,
      anexos: [],
      origem: origemTarefa(e.tarefa_id),
    })),
  ];

  return itens.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
};
